import React, { useEffect, useState, useRef } from "react";
import Uppy from "@uppy/core";
import { Dashboard } from "@uppy/react";
import ProgressBar from "@uppy/progress-bar";
import { MdDeleteOutline } from "react-icons/md";
import { MdCheckCircle } from "react-icons/md";
// Import Uppy styles
import "@uppy/core/dist/style.css";
import "@uppy/dashboard/dist/style.css";
import "@uppy/progress-bar/dist/style.css";
import * as pdfjs from "pdfjs-dist/webpack";
import * as XLSX from 'xlsx';

// Component for extracting text from PDF and Excel files
function TextExtractor({ allowedFileTypes = [".pdf", ".xlsx", ".xls", ".csv"], onSuccess, onDelete, hideExtraInfo, heightOfUppy=190, loading=false }) {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedFileURL, setUploadedFileURL] = useState('https://i.ibb.co/Xfp8Bp7Y/Microsoft-Excel-2013-2019-logo-svg.png');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const progressBarRef = useRef(null);
  const [_extractionComplete, setExtractionComplete] = useState(false);

  // Initialize Uppy instance with file restrictions
  const uppy = React.useMemo(() => {
    return new Uppy({
      restrictions: {
        maxNumberOfFiles: 1,
        allowedFileTypes,
      },
      autoProceed: true,
    });
  }, [allowedFileTypes]);

  // Extract text from Excel/CSV files
  const extractTextFromExcel = async (file) => {
    console.info('Extracting text from Excel/CSV file');
    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer);
      let text = "";

      workbook.SheetNames.forEach(sheetName => {
        const worksheet = workbook.Sheets[sheetName];
        const sheetData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        sheetData.forEach(row => {
          if (row.length > 0) {
            text += row.join(" ") + "\n";
          }
        });
        text += "\n";
      });

      return text.trim();
    } catch (error) {
      throw error;
    }
  };

  // Extract text from PDF or Excel files
  const extractText = async (file) => {
    console.info('Extracting text from file');
    try {
      const fileType = file.name.split('.').pop().toLowerCase();
      let text = "";

      if (fileType === 'pdf') {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjs.getDocument(arrayBuffer).promise;

        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const content = await page.getTextContent();
          const pageText = content.items.map((item) => item.str).join(" ");
          text += `${pageText}\n\n`;
        }
      } else if (['xlsx', 'xls', 'csv'].includes(fileType)) {
        text = await extractTextFromExcel(file);
      }

      setExtractionComplete(true);
      return text;

    } catch (error) {
      onSuccess("Failed to extract text from the file.");
    }
  };

  // Handle file upload and text extraction
  useEffect(() => {
    console.info('Setting up Uppy event handlers');
    if (!uppy.getPlugin("ProgressBar") && progressBarRef.current) {
      uppy.use(ProgressBar, {
        target: progressBarRef.current,
        hideAfterFinish: false,
      });
    }

    uppy.on("upload-progress", (file, progress) => {
      setUploadProgress(progress.progress);
    });

    uppy.on("complete", async (result) => {
      if (result.successful.length > 0) {
        const uploadedFile = result.successful[0].data;
        setUploadedFile(uploadedFile.name);

        setUploading(true);
        try {
          const extractedText = await extractText(result.successful[0].data);
          if (onSuccess) {
            onSuccess(extractedText);
          }
        } catch (error) {
          console.error("Error during file upload:", error);
        } finally {
          setUploading(false);
        }
      }
    });
  }, [uppy, onSuccess]);

  // Handle file deletion
  const handleDelete = () => {
    console.info('Deleting uploaded file');
    setUploadedFile(null);
    setUploadedFileURL(null);
    setUploadProgress(0);
    onDelete()
  };

  return (
    <div className=" mx-auto flex flex-col gap-4">
      {!uploading && !uploadedFile && (
        <div className={`relative ${loading ? 'pointer-events-none opacity-50' : ''}`}>
          <Dashboard
            uppy={uppy}
            height={heightOfUppy}
            hideUploadButton
            proudlyDisplayPoweredByUppy={false}
          />
        </div>
      )}

      {uploading && (
        <div className=" w-full  border p-4 -mt-4  text-gray-700 flex justify-between items-center">
          <div className="flex items-center gap-2">
            {uploadedFile}
            <div className=" w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
              <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
            </div>
          </div>
        </div>
      )}
      <div ref={progressBarRef} className="uppy-progress"></div>

      {(!uploading && !uploadedFile) && !hideExtraInfo &&  (
        <p className="text-gray-500 text-sm mb-2">  Supported format: Excel, CSV</p>
      )}

      {uploadedFile && !uploading && (
        <div className=" border p-4   mb-3  text-gray-700 flex justify-between items-center">
          <div className="flex items-center gap-2 relative">
            <div className="relative h-10 w-10">
              <img src={uploadedFileURL} alt=".." className="h-full w-full object-cover rounded border border-gray-200 shadow-sm" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <MdCheckCircle className="text-green-500 text-xl" />
              </div>
            </div>
            {uploadedFile}
          </div>
          <div onClick={handleDelete} className={loading ? 'pointer-events-none opacity-50' : ''}>
            <MdDeleteOutline className="text-xl cursor-pointer" />
          </div>
        </div>
      )}
    </div>
  );
}

export default TextExtractor;